import { allegroApiService } from '../../shared/services/allegroApi';
import { Product } from '../models/Product';
import { containsString } from '../../shared/utils/stringUtils';
import config from '../../config/environment';

// Cache dla produktów
const productCache = new Map();
const cacheTimeout = config.PERFORMANCE.CACHE_DURATION;

// Performance monitoring
const logPerformance = (operation, duration, dataSize = null) => {
  if (config.IS_DEVELOPMENT) {
    console.log(`Performance [${operation}]:`, {
      duration: `${duration.toFixed(2)}ms`,
      dataSize: dataSize ? `${dataSize} items` : null,
      timestamp: new Date().toISOString(),
    });
  }
};

// Serwis domenowy dla zarządzania magazynem
export class InventoryService {
  // Pobieranie produktów z paginacją
  async getProducts(params = {}) {
    const startTime = performance.now();
    
    try {
      console.log('🔍 InventoryService.getProducts called with params:', params);
      
      const cacheKey = JSON.stringify(params);
      const cached = productCache.get(cacheKey);
      
      if (cached && Date.now() - cached.timestamp < cacheTimeout) {
        const duration = performance.now() - startTime;
        logPerformance('getProducts (cached)', duration, cached.data.products.length);
        return cached.data;
      }

      const response = await allegroApiService.getProducts(params);
      console.log('📦 Response from allegroApiService:', response);

      // Sprawdź czy response.products jest tablicą
      let productsArray = [];
      if (Array.isArray(response.products)) {
        productsArray = response.products;
      } else if (response.products && response.products.content && Array.isArray(response.products.content)) {
        productsArray = response.products.content;
      }

      const result = {
        products: productsArray.map(productData => Product.fromApi(productData)),
        pagination: response.pagination,
      };

      // Cache the result
      productCache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      const duration = performance.now() - startTime;
      logPerformance('getProducts', duration, result.products.length);

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      logPerformance('getProducts (error)', duration);
      console.error('Błąd podczas pobierania produktów:', error);
      throw new Error('Nie udało się pobrać produktów');
    }
  }

  // Pobieranie wszystkich produktów (bez paginacji) - tylko dla małych zestawów danych
  async getAllProducts() {
    const startTime = performance.now();
    
    try {
      const productsData = await allegroApiService.getAllProducts();
      const result = productsData.map(productData => Product.fromApi(productData));
      
      const duration = performance.now() - startTime;
      logPerformance('getAllProducts', duration, result.length);
      
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      logPerformance('getAllProducts (error)', duration);
      console.error('Błąd podczas pobierania produktów:', error);
      throw new Error('Nie udało się pobrać produktów');
    }
  }

  // Czyszczenie cache
  clearCache() {
    productCache.clear();
    if (config.IS_DEVELOPMENT) {
      console.log('Cache cleared');
    }
  }

  // Invalidate cache for specific product
  invalidateProductCache(productId) {
    let invalidatedCount = 0;
    for (const [key, value] of productCache.entries()) {
      const params = JSON.parse(key);
      if (params.productId === productId) {
        productCache.delete(key);
        invalidatedCount++;
      }
    }
    
    if (config.IS_DEVELOPMENT && invalidatedCount > 0) {
      console.log(`Invalidated ${invalidatedCount} cache entries for product ${productId}`);
    }
  }

  // Dodawanie nowego produktu
  async addProduct(productData) {
    try {
      const response = await allegroApiService.addProduct(productData);
      return Product.fromApi(response);
    } catch (error) {
      console.error('Błąd podczas dodawania produktu:', error);
      throw new Error('Nie udało się dodać produktu');
    }
  }

  // Aktualizacja produktu
  async updateProduct(productId, productData) {
    try {
      const response = await allegroApiService.updateProduct(productId, productData);
      return Product.fromApi(response);
    } catch (error) {
      console.error('Błąd podczas aktualizacji produktu:', error);
      throw new Error('Nie udało się zaktualizować produktu');
    }
  }

  // Usuwanie produktu
  async deleteProduct(productId) {
    try {
      await allegroApiService.deleteProduct(productId);
      return true;
    } catch (error) {
      console.error('Błąd podczas usuwania produktu:', error);
      throw new Error('Nie udało się usunąć produktu');
    }
  }

  // Pobieranie produktu po ID
  async getProduct(id) {
    try {
      const products = await this.getProducts();
      return products.find(p => p.id === id);
    } catch (error) {
      console.error('Błąd podczas pobierania produktu:', error);
      throw new Error('Nie udało się pobrać produktu');
    }
  }

  // Aktualizacja stanu magazynowego
  async updateProductQuantity(productId, newQuantity) {
    try {
      const product = await this.getProduct(productId);
      if (!product) {
        throw new Error('Produkt nie został znaleziony');
      }

      const updatedProduct = await allegroApiService.updateProduct(productId, {
        ...product.toApi(),
        quantity: newQuantity,
      });

      return Product.fromApi(updatedProduct);
    } catch (error) {
      console.error('Błąd podczas aktualizacji stanu:', error);
      throw new Error('Nie udało się zaktualizować stanu magazynowego');
    }
  }

  // Wyszukiwanie produktów
  async searchProducts(query, filters = {}) {
    try {
      const allProducts = await this.getProducts();
      
      return allProducts.filter(product => {
        const matchesSearch = containsString(product.name, query) ||
                             containsString(product.sku, query) ||
                             containsString(product.externalId, query);
        
        const matchesStatus = !filters.status || product.status === filters.status;
        const matchesEnabled = !filters.enabled || product.getActiveShopsCount() > 0;
        
        return matchesSearch && matchesStatus && matchesEnabled;
      });
    } catch (error) {
      console.error('Błąd podczas wyszukiwania:', error);
      throw new Error('Nie udało się wyszukać produktów');
    }
  }

  // Pobieranie statystyk magazynowych
  async getInventoryStats() {
    try {
      const products = await this.getProducts();
      
      const stats = {
        totalProducts: products.length,
        available: products.filter(p => p.status === 'available').length,
        lowStock: products.filter(p => p.status === 'low_stock').length,
        outOfStock: products.filter(p => p.status === 'out_of_stock').length,
        enabledProducts: products.filter(p => p.getActiveShopsCount() > 0).length,
        totalQuantity: products.reduce((sum, p) => sum + p.quantity, 0),
      };

      return stats;
    } catch (error) {
      console.error('Błąd podczas pobierania statystyk:', error);
      throw new Error('Nie udało się pobrać statystyk');
    }
  }

  // Eksport danych magazynowych
  async exportInventory(format = 'csv') {
    try {
      const data = await allegroApiService.exportData(format);
      return data;
    } catch (error) {
      console.error('Błąd podczas eksportu:', error);
      throw new Error('Nie udało się wyeksportować danych');
    }
  }

  // Pobieranie kont z Twojego backendu
  async getAccounts() {
    try {
      const accounts = await allegroApiService.getAccounts();
      return accounts;
    } catch (error) {
      console.error('Błąd podczas pobierania kont:', error);
      throw new Error('Nie udało się pobrać kont');
    }
  }
}

// Singleton instance
export const inventoryService = new InventoryService(); 